import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons/faBadgeCheck';
import { faOctagonXmark } from '@fortawesome/pro-solid-svg-icons/faOctagonXmark';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { MprcClickable, MprcText } from "components";
import { EnrollmentViewDto, EnrollStatus } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { MprcCardCourseIcon } from 'views/lms/student/EnrollmentView/Course/ContentComponents/common/CourseViewComponents';
import { WaitingApprovalDrawer } from 'views/lms/student/EnrollmentView/drawers/WaitingApprovalDrawer';

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    variant: "inline" | "block";
    data: EnrollmentViewDto;
}

/////////////////////////////////////////////////////////////////////////////////////

export const EnrollmentStatus: React.FC<IProps> = (props) => {

    const { variant, data } = props;
    const { t } = useTranslation("lms");
    const [showApproverDrawer, setShowApproverDrawer] = useState<boolean>(false);

    const today = new Date();
    today.setHours(23);
    today.setMinutes(59);
    today.setSeconds(59);

    const missingToReviewDays = data.canReview && data.reviewUntil ? Formaters.dateDiffDays(data.reviewUntil, today) : null;

    const RenderBlock = (
        <>
            {
                data.status == EnrollStatus.WaitingPreApproval
                    ? <Stack direction="column" spacing={-0.5}>
                        <MprcClickable onClick={() => setShowApproverDrawer(true)}>
                            <Typography variant={"body2"} fontWeight="bold" textTransform="uppercase" color="warning.main" component="p">
                                {t("Student.EnrollmentView.WaitingPreApprovalText")}
                            </Typography>
                        </MprcClickable>
                    </Stack>
                    : data.status == EnrollStatus.WaitingScore
                        ? <Stack direction="column" spacing={-0.5}>
                            <Typography variant={"body2"} fontWeight="bold" textTransform="uppercase" color="warning.main" component="p">
                                {t("Student.EnrollmentView.WaitingScoreText")}
                            </Typography>
                            {data.expireDate &&
                                <Typography variant="caption">
                                    {t("Student.EnrollmentView.FinalizationText")}&nbsp;
                                    {Formaters.performaDate(data.expireDate, { dayOfWeek: false, preposition: "always" })}
                                </Typography>
                            }
                        </Stack>
                        :
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            {
                                data.certificateExpired
                                    ? (
                                        <>
                                            <MprcText color="muted">
                                                <MprcCardCourseIcon icon={faBadgeCheck} />
                                            </MprcText>
                                            <div>
                                                <MprcText color="muted">
                                                    <Typography variant="h4" component="p" color="success">
                                                        {t("Student.EnrollmentView.CertificateExpiredText")}
                                                    </Typography>
                                                </MprcText>
                                                <Typography variant="caption" component="p">
                                                    {t("Student.EnrollmentView.FinishedAtText")} {Formaters.performaDate(data.finalizationDate!, { preposition: "none" })}
                                                    {
                                                        data.certificateValidUntil &&
                                                        <>
                                                            <br />
                                                            {t("Student.EnrollmentView.CertificateExpiredOnText", { date: Formaters.dayMonth(data.certificateValidUntil) })}
                                                        </>
                                                    }
                                                </Typography>
                                            </div>
                                        </>
                                    )
                                    : (
                                        <>
                                            {
                                                data.status == EnrollStatus.Approved &&
                                                <>
                                                    <MprcText color="success">
                                                        <MprcCardCourseIcon icon={faBadgeCheck} />
                                                    </MprcText>
                                                    <div>
                                                        <MprcText color="success">
                                                            <Typography variant="h4" component="p" color="success">
                                                                {
                                                                    data.hasScore
                                                                        ? t("Student.EnrollmentView.ApprovedText")
                                                                        : t("Student.EnrollmentView.CompletedText")
                                                                }
                                                            </Typography>
                                                        </MprcText>
                                                        <Typography variant="caption" component="p">
                                                            {t("Student.EnrollmentView.FinishedAtText")} {Formaters.performaDate(data.finalizationDate!, { preposition: "none" })}
                                                            {missingToReviewDays != null && missingToReviewDays >= 0 &&
                                                                <>
                                                                    <br />
                                                                    {missingToReviewDays === 0
                                                                        ? t("Student.EnrollmentView.ReviewDaysUntilTodayText")
                                                                        : t("Student.EnrollmentView.MissingReviewDaysText", { count: missingToReviewDays })
                                                                    }
                                                                </>
                                                            }
                                                            {data.hasCertificate && data.certificateValidUntil &&
                                                                <>
                                                                    <br />
                                                                    {t("Student.EnrollmentView.CertificateValidUntilText", { date: Formaters.dayMonth(data.certificateValidUntil) })}
                                                                </>
                                                            }
                                                        </Typography>
                                                    </div>
                                                </>
                                            }
                                            {
                                                data.status == EnrollStatus.Reproved &&
                                                <>
                                                    <MprcText color="error">
                                                        <MprcCardCourseIcon icon={faOctagonXmark} />
                                                    </MprcText>
                                                    <div>
                                                        <MprcText color="error">
                                                            <Typography variant="h4" component="p">
                                                                {data.hasScore ? t("Student.EnrollmentView.ReprovedText") : t("Student.EnrollmentView.NotCompletedText")}
                                                            </Typography>
                                                        </MprcText>
                                                        <Typography variant="caption" component="p">
                                                            {
                                                                data.reprovedByExpireDate && data.expireDate
                                                                    ? t("Student.EnrollmentView.DeadlineExpiredText", { date: Formaters.dayMonth(data.expireDate) })
                                                                    : t("Student.EnrollmentView.ConcludedAtText", { date: Formaters.performaDate(data.finalizationDate!, { preposition: "none" }) })
                                                            }

                                                            {
                                                                missingToReviewDays != null && missingToReviewDays >= 0 &&
                                                                <>
                                                                    <br />
                                                                    {missingToReviewDays === 0
                                                                        ? t("Student.EnrollmentView.ReviewDaysUntilTodayText")
                                                                        : t("Student.EnrollmentView.MissingReviewDaysText", { count: missingToReviewDays })
                                                                    }
                                                                </>
                                                            }

                                                            {
                                                                data.nextAutoEnrollDate &&
                                                                <>
                                                                    <br />
                                                                    {t("Student.EnrollmentView.NextAutoEnrollText", { date: Formaters.performaDate(data.nextAutoEnrollDate) })}
                                                                </>
                                                            }
                                                        </Typography>
                                                    </div>
                                                </>
                                            }
                                        </>
                                    )
                            }
                        </Stack>
            }
        </>
    );

    const RenderInline = (
        <Stack direction="row" spacing={1} alignItems="center">
            {
                data.status == EnrollStatus.WaitingPreApproval
                    ? <MprcClickable onClick={() => setShowApproverDrawer(true)}>
                        <Typography
                            variant={"captionHeader"}
                            component="span"
                            fontWeight="bold"
                            color={"warning.main"}
                        >
                            {t("Student.EnrollmentView.WaitingPreApprovalText")}
                        </Typography>
                    </MprcClickable>
                    : data.status == EnrollStatus.WaitingScore
                        ? <>
                            <Typography
                                variant={"captionHeader"}
                                component="span"
                                fontWeight="bold"
                                color={"warning.main"}
                            >
                                {t("Student.EnrollmentView.WaitingScoreText")}
                            </Typography>
                            {data.expireDate &&
                                <Typography variant="caption" component="span">
                                    -&nbsp;{t("Student.EnrollmentView.FinalizationText")}&nbsp;
                                    {Formaters.performaDate(data.expireDate, { dayOfWeek: true, preposition: "always" })}
                                </Typography>
                            }
                        </>
                        : data.certificateExpired
                            ? (
                                <>
                                    <MprcText color="muted">
                                        <FontAwesomeIcon icon={faBadgeCheck} fontSize={15} fixedWidth />
                                    </MprcText>
                                    <Typography variant="captionHeader" fontWeight="bold" component="span">
                                        <MprcText color="muted">{t("Student.EnrollmentView.CertificateExpiredText")}</MprcText>
                                    </Typography>
                                    <Typography variant="body2" component="span">
                                        {`${t("shared:Literal.At")} ${Formaters.performaDate(data.certificateValidUntil!, { preposition: "none" })}`}
                                    </Typography>
                                </>
                            )
                            : (
                                <>
                                    {
                                        data.status == EnrollStatus.Approved &&
                                        <>
                                            <MprcText color="success">
                                                <FontAwesomeIcon icon={faBadgeCheck} fontSize={15} fixedWidth />
                                            </MprcText>
                                            <div>
                                                <Typography variant="captionHeader" fontWeight="bold" component="span">
                                                    <MprcText color="success">
                                                        {
                                                            data.hasScore
                                                                ? t("Student.EnrollmentView.ApprovedText")
                                                                : t("Student.EnrollmentView.CompletedText")
                                                        }
                                                    </MprcText>
                                                </Typography>
                                                <Typography variant="caption" component="span">
                                                    &nbsp;&mdash;&nbsp;
                                                    {t("Student.EnrollmentView.FinishedAtText")} {Formaters.performaDate(data.finalizationDate!, { preposition: "none" })}
                                                </Typography>
                                            </div>
                                        </>
                                    }
                                    {
                                        data.status == EnrollStatus.Reproved &&
                                        <>
                                            <MprcText color="error">
                                                <FontAwesomeIcon icon={faOctagonXmark} fontSize={15} fixedWidth />
                                            </MprcText>
                                            <Typography variant="captionHeader" fontWeight="bold" component="span">
                                                <MprcText color="error">
                                                    {
                                                        data.hasScore
                                                            ? t("Student.EnrollmentView.ReprovedText")
                                                            : t("Student.EnrollmentView.NotCompletedText")
                                                    }
                                                </MprcText>
                                            </Typography>
                                        </>
                                    }
                                </>
                            )
            }
        </Stack>
    );

    return <>
        {
            showApproverDrawer &&
            <WaitingApprovalDrawer
                enrollmentId={data.enrollmentId}
                trainingName={data.trainingName}
                onClose={() => setShowApproverDrawer(false)}
            />
        }

        {variant == "inline" ? RenderInline : RenderBlock}
    </>;
};
